/**
 * Host Lobby Component
 *
 * Pre-game lobby showing participants and start button.
 */

import type { LiveQuizParticipant } from '../../../types/liveQuiz';
import { GamePin } from '../shared/GamePin';

interface HostLobbyProps {
  gamePin: string;
  participants: LiveQuizParticipant[];
  readyCount: number;
  minPlayers?: number;
  onStart: () => void;
  onKick: (participantId: string, reason?: string) => void;
}

export function HostLobby({
  gamePin,
  participants,
  readyCount,
  minPlayers = 1,
  onStart,
  onKick,
}: HostLobbyProps) {
  const activeParticipants = participants.filter((p) => p.status === 'connected');
  const canStart = activeParticipants.length >= minPlayers;

  return (
    <div className="max-w-4xl mx-auto">
      {/* PIN Display */}
      <div className="text-center mb-12">
        <p className="text-purple-300 mb-4">
          Players join at <span className="font-mono text-white">yoursite.com/play</span>
        </p>
        <GamePin pin={gamePin} size="lg" />
      </div>

      {/* Player count */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 rounded-full">
          <svg className="w-6 h-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          <span className="text-2xl font-bold text-white">{activeParticipants.length}</span>
          <span className="text-purple-300">
            {activeParticipants.length === 1 ? 'player' : 'players'} joined
          </span>
        </div>
      </div>

      {/* Participants Grid */}
      {activeParticipants.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-12">
          {activeParticipants.map((participant, index) => (
            <div
              key={participant.id}
              className="group relative bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-all animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Avatar */}
              <div className="text-4xl text-center mb-2">
                {participant.avatar || '😀'}
              </div>

              {/* Nickname */}
              <p className="text-sm font-medium text-white text-center truncate">
                {participant.nickname}
              </p>

              {/* Ready indicator */}
              {participant.isReady && (
                <div className="absolute top-2 right-2 w-3 h-3 bg-green-500 rounded-full" title="Ready" />
              )}

              {/* Kick button (on hover) */}
              <button
                onClick={() => onKick(participant.id)}
                className="absolute top-1 right-1 p-1 bg-red-500/80 hover:bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove player"
              >
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="inline-block animate-bounce">
            <svg className="w-16 h-16 text-purple-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <p className="text-xl text-purple-300 mt-4">Waiting for players to join...</p>
          <p className="text-sm text-purple-400 mt-2">Share the game PIN above</p>
        </div>
      )}

      {/* Start Button */}
      <div className="text-center">
        <button
          onClick={onStart}
          disabled={!canStart}
          className={`px-12 py-5 text-2xl font-bold rounded-2xl transition-all transform ${
            canStart
              ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white shadow-lg shadow-green-500/30 hover:scale-105'
              : 'bg-gray-600 text-gray-400 cursor-not-allowed'
          }`}
        >
          {canStart ? 'Start Game' : `Need ${minPlayers - activeParticipants.length} more player(s)`}
        </button>

        {canStart && activeParticipants.length > 0 && (
          <p className="text-sm text-purple-400 mt-4">
            {readyCount} of {activeParticipants.length} players ready
          </p>
        )}
      </div>

      {/* Tips */}
      <div className="mt-12 p-4 bg-purple-900/30 rounded-xl border border-purple-500/20">
        <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide mb-2">
          Tips
        </h3>
        <ul className="text-sm text-purple-400 space-y-1">
          <li>- Display this screen for all players to see the game PIN</li>
          <li>- Wait for all expected players to join before starting</li>
          <li>- You can remove disruptive players by hovering over their name</li>
        </ul>
      </div>
    </div>
  );
}

export default HostLobby;
